import { useEffect, useMemo, useState, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MagnifyingGlass, ArrowClockwise, DownloadSimple, Prohibit, CheckCircle, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function AdminCustomers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("id, email, full_name, phone, phone_country, role, is_blocked, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const ch = supabase
      .channel("admin-customers")
      .on("postgres_changes", { event: "*", schema: "public", table: "profiles" }, () => load())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((c) => {
      if (filter === "customers" && c.role !== "customer") return false;
      if (filter === "admins" && c.role !== "admin") return false;
      if (filter === "blocked" && !c.is_blocked) return false;
      if (!query) return true;
      return `${c.email || ""} ${c.full_name || ""} ${c.phone || ""}`.toLowerCase().includes(query);
    });
  }, [rows, q, filter]);

  const toggleBlock = async (c) => {
    const next = !c.is_blocked;
    const { error } = await supabase.from("profiles").update({ is_blocked: next }).eq("id", c.id);
    if (error) toast.error(error.message);
    else { toast.success(next ? "Customer blocked" : "Customer unblocked"); load(); }
  };

  const remove = async (c) => {
    if (c.role === "admin") return toast.error("Cannot delete admin accounts");
    if (!window.confirm(`Delete profile for ${c.email}?`)) return;
    const { error } = await supabase.from("profiles").delete().eq("id", c.id);
    if (error) toast.error(error.message);
    else { toast.success("Profile deleted"); load(); }
  };

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const header = ["email", "full_name", "phone", "role", "is_blocked", "created_at"];
    const csv = [header.join(","), ...filtered.map((c) => header.map((k) => `"${String(c[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `customers-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="Customers">
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-5">
        <div className="flex-1 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, name, phone…" className="pl-9 h-10 rounded-xl bg-white" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-10 rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="customers">Customers</SelectItem>
              <SelectItem value="admins">Admins</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-xl h-10 border-slate-200"><ArrowClockwise size={14} weight="bold" className="mr-1.5" /> Refresh</Button>
          <Button onClick={exportCsv} className="rounded-xl h-10 bg-navy-deep hover:bg-navy text-white"><DownloadSimple size={14} weight="bold" className="mr-1.5" /> Export CSV</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[0,1,2].map((i) => <div key={i} className="h-14 rounded-xl bg-slate-100 animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No customers found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 font-mono-data">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">User</th>
                  <th className="text-left px-5 py-3 font-semibold">Email</th>
                  <th className="text-left px-5 py-3 font-semibold">Phone</th>
                  <th className="text-left px-5 py-3 font-semibold">Role</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">Joined</th>
                  <th className="text-right px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-cta-gradient grid place-items-center text-white font-display font-bold text-[13px] uppercase">{(c.full_name || c.email || "?")[0]}</div>
                        <div className="font-semibold text-navy-deep">{c.full_name || "—"}</div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{c.email}</td>
                    <td className="px-5 py-3 text-slate-500 font-mono-data text-[12px]">{c.phone || "—"}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10.5px] uppercase tracking-[0.14em] font-mono-data font-semibold px-2 py-1 rounded-full ${c.role === "admin" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-100 text-slate-700"}`}>{c.role}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-[10.5px] uppercase tracking-[0.14em] font-mono-data font-semibold px-2 py-1 rounded-full ${c.is_blocked ? "bg-red-50 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>{c.is_blocked ? "Blocked" : "Active"}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-[12px]">{fmtDate(c.created_at)}</td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => toggleBlock(c)} className={`h-8 w-8 rounded-lg grid place-items-center ${c.is_blocked ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-500 hover:text-red-500 hover:bg-red-50"}`} title={c.is_blocked ? "Unblock" : "Block"}>
                          {c.is_blocked ? <CheckCircle size={14} /> : <Prohibit size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
